import type { CreditAmount, SystemSettingsDoc, UserAccountDoc } from '../firestore/schemas';

export type Tier = 'free' | 'paid' | 'comped';

export function computeTier(userAccount: UserAccountDoc | undefined): Tier {
  if (userAccount?.compedAt) return 'comped';
  const subscription = userAccount?.stripeSubscription;
  if (subscription?.status === 'active' || subscription?.status === 'trialing') return 'paid';
  if (subscription?.status === 'past_due' && subscription.hasPaidInvoice === true) return 'paid';
  return 'free';
}

/**
 * Whether a subscription in this status is live: Stripe can still charge the customer for it.
 * Takes any string because Stripe's own status type admits statuses added after the SDK version.
 */
export const isLiveSubscriptionStatus = (status: string) => status === 'trialing' || status === 'active' || status === 'past_due';

/** For each field on the account that is not a current `CreditAmount`, its acceptance default: the AI Preview quota from settings, an empty monthly quota and Joule balance. */
export const missingAccountQuotas = (
  account: UserAccountDoc | undefined,
  systemSettings: Pick<SystemSettingsDoc, 'aiPreviewCredits'>
): Partial<Pick<UserAccountDoc, 'aiPreviewQuota' | 'monthlyQuota' | 'creditBalance'>> => ({
  ...(isCreditAmount(account?.aiPreviewQuota) ? {} : { aiPreviewQuota: { total: systemSettings.aiPreviewCredits, used: 0 } }),
  ...(isCreditAmount(account?.monthlyQuota) ? {} : { monthlyQuota: { total: 0, used: 0 } }),
  ...(isCreditAmount(account?.creditBalance) ? {} : { creditBalance: { total: 0, used: 0 } }),
});

/** A non-null object whose `total` and `used` are both finite numbers; other keys are ignored. */
export function isCreditAmount(value: unknown): value is CreditAmount {
  if (typeof value !== 'object' || value === null) return false;
  const { total, used } = value as Record<string, unknown>;
  return Number.isFinite(total) && Number.isFinite(used);
}

/** Joules left of `amount`'s `share` of its total; a missing or malformed amount counts as empty. */
export const creditsLeft = (amount: CreditAmount | undefined, share = 1) =>
  isCreditAmount(amount) ? amount.total * share - amount.used : 0;

/** Whole Joules left of `amount`'s `share` of its total, never below zero; a missing or malformed amount counts as empty. */
export const wholeCreditsLeft = (amount: CreditAmount | undefined, share = 1) =>
  Math.max(0, Math.floor(creditsLeft(amount, share)));

/**
 * The percent of `amount`'s `share` of its total left, rounded down so an amount with anything spent never reads
 * as full, but at least 1 while a whole Joule is left (AI is still allowed); a missing or malformed amount counts as empty.
 */
export const creditsLeftPercent = (amount: CreditAmount | undefined, share = 1) => {
  const left = wholeCreditsLeft(amount, share);
  if (!isCreditAmount(amount) || left < 1) return 0;
  return Math.max(1, Math.min(100, Math.floor((left / (amount.total * share)) * 100)));
};

/** Whether a whole Joule is left, the rule the AI gate allows on. */
export const hasCreditsLeft = (amount: CreditAmount | undefined, share = 1) => creditsLeft(amount, share) >= 1;

/** The share of the monthly quota a paid user may spend: the grace share while `past_due`, else all of it. */
export const monthlyQuotaShare = (
  account: UserAccountDoc | undefined,
  systemSettings: Pick<SystemSettingsDoc, 'gracePeriodQuotaPercent'>
) => (account?.stripeSubscription?.status === 'past_due' ? systemSettings.gracePeriodQuotaPercent / 100 : 1);
